import { Paper, Skeleton, Stack, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { KpiTicketQuelle, TicketZeitraum, Vorgangstyp } from "@msc/shared";

import { useKpiTicketSla, type TicketEingrenzung } from "../../../hooks/useKpiTickets.js";
import { KundenTabelle } from "./KundenTabelle.js";
import { MonatsDiagramm } from "./MonatsDiagramm.js";
import { ReaktionTabelle, type ReaktionKlick } from "./ReaktionTabelle.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./TicketAuswahlModal.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";
import { WiedereroeffnungKarte } from "./WiedereroeffnungKarte.js";

/** SR-06 als Block: Kopf, Tabelle, Liste. */
export function ReaktionBlock({ segmentId, year, eingrenzung }: { segmentId: string; year: number; eingrenzung?: TicketEingrenzung }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [zeitraum, setZeitraum] = useState<TicketZeitraum | null>(null);
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const { data, isLoading, isError } = useKpiTicketSla(segmentId, year, zeitraum, vorgangstyp, eingrenzung);
  const zeige = (klick: ReaktionKlick, abteilungId: string, abteilung: string) => {
    if (!data) return;
    const abschnitt = (quelle: KpiTicketQuelle) => ({
      titel: t(quelle === "sla_verletzt" ? "pages.kpi.tickets.reaktion.colVerletzt" : "pages.kpi.tickets.reaktion.colRechtzeitig"),
      abfrage: { quelle, ...data.zeitraum, vorgangstyp, abteilungId, ...eingrenzung },
    });
    setAuswahl({
      titel: abteilung,
      abschnitte: klick === "alle" ? [abschnitt("sla_eingehalten"), abschnitt("sla_verletzt")] : [abschnitt(klick)],
    });
  };
  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf titel={t("pages.kpi.tickets.reaktion.titel")} spiegel={data?.spiegel} vorgangstyp={vorgangstyp} onVorgangstyp={setVorgangstyp} zeitraum={zeitraum} onZeitraum={setZeitraum} />
      <Text size="xs" c="dimmed" mb="xs">{t("pages.kpi.tickets.reaktion.hinweis")}</Text>
      {isLoading ? <Skeleton height={200} /> : isError || !data ? <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text> : <ReaktionTabelle daten={data} onKlick={zeige} />}
      <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={data?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
    </Paper>
  );
}

/**
 * Die vier Ticket-Blöcke unter den Geldkurven (Lieferung 2.1b, Ablösung eazyBI).
 * Jeder Block filtert für sich; Grundmenge sind die Kunden des Bereichs, deren
 * Beauftragung mit einer Jira-Kundenabteilung verknüpft ist.
 */
export function TicketBloecke({ segmentId, year }: { segmentId: string; year: number }) {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <Text fw={700}>{t("pages.kpi.tickets.ueberschrift")}</Text>
      <MonatsDiagramm segmentId={segmentId} year={year} />
      <WiedereroeffnungKarte segmentId={segmentId} year={year} />
      <KundenTabelle segmentId={segmentId} year={year} />
      <ReaktionBlock segmentId={segmentId} year={year} />
    </Stack>
  );
}
